import React, { memo, useState } from "react";
import { useHistory } from "react-router-dom";
import { Flex } from "rebass";
import { colors } from "../../../../theme/colors";
import styled from "styled-components";
import { Headline } from "../../../text";
import { getClipTitle } from "../../../molecules/clip/utils";
import { ReactComponent as StudioBadge } from "../../../../assets/images/badges/studio_icon.svg";
import { ReactComponent as RemixIcon } from "../../../../assets/images/badges/remix-icon.svg";
import DownloadClip from "../../../atoms/form/button/downloadClip";
import { YouTubeShare } from "../../../../MVVM/Components/Button/PlatformShare/Youtube";
import { clipHasStudio, trackClipDownload } from "../../../../utils";
import { ShareModal } from "../../modalContents/share";
import { Body } from "../../../text";
import Modal from "../../../wrappers/modal";
import { FiAlertCircle } from "react-icons/fi";
import { useGetUser } from "../../../../userStore";
import { TikTokUpload } from "../../../../MVVM/Components/Button/PlatformShare/TikTok";

const StyledIcon = styled(FiAlertCircle)`
  color: ${(props) => props.theme.colors.error};
`;

const StyledHeadline = styled(Headline)`
  font-size: 1.125em;

  @media (max-width: ${(props) => props.theme.breaks.small}) {
    font-size: 1em;
  }
`;

const CardWhole = styled.div`
  display: block;

  .title {
    transition: color 0.1s ease-in-out;
  }
`;

const Card = styled.div`
  display: block;
  padding-top: 170%;
  position: relative;
  overflow: hidden;
  border-radius: 14px 14px 0px 0px;
  height: 0;
  background-image: ${(props) =>
    `url(${process.env.REACT_APP_S3_CLIP_BUCKET}/${props.thumbnail})`};
  background-size: cover;
  &:hover {
    cursor: pointer;

    .title {
      color: ${(props) => props.theme.colors.envy};
    }
  }
`;

const ErrorCard = styled.div`
  display: block;
  padding-top: 170%;
  position: relative;
  overflow: hidden;
  border-radius: 16px;
  height: 0;
  background: #18252d;
  background-size: cover;
  border-color: #3b3737;
  border-width: 2px;
  border-style: solid;
`;

const StyledFlex = styled(Flex)`
  position: absolute;
  left: 0;
  bottom: 0;
  right: 0;
`;

const StyledOptions = styled(Flex)`
  background: ${(props) => props.theme.colors.midnightAlpha90};
  border-radius: 0px 0px 14px 14px;
  padding: 10px;
`;

const StyledLine = styled(Flex)`
  background: ${(props) => props.theme.colors.chalkAlpha20};
  border-radius: 999px;
  height: 2px;
  width: 50px;
`;

const StyledError = styled(Flex)`
  position: absolute;
  top: 50%;
  width: 100%;
  
  @media (max-width: ${(props) => props.theme.breaks.small}) {
    top: 40%;
  
`;

const ShadowBackground = styled(Flex)`
  position: absolute;
  left: 0;
  bottom: 0;
  right: 0;
  top: 0;
  background-image: linear-gradient(
    to top,
    ${colors.midnightAlpha90},
    rgba(255, 255, 255, 0) 60%
  );
`;

const MobileClipCard = memo(({ clip, optionsProps, ...props }) => {
  const hasStudio = clipHasStudio(clip);
  const isRemixed = clip.remix?.isRemixed;
  const [isShareModalOpen, setShareModalOpen] = useState(false);
  const user = useGetUser();
  const history = useHistory();
  // changing the key to force remount the upload components e.g to clear the internal state to allow reuploading
  const [socialPlatformKeys, setSocialPlatformKeys] = useState({
    YouTube: `YouTube-${Date.now()}`,
    TikTok: `TikTok-${Date.now()}`,
  });
  const onSocialPlatformReset = (platform) => {
    setSocialPlatformKeys((current) => ({
      ...current,
      [platform]: `${platform}-${Date.now()}`,
    }));
  };

  if (clip.mobile[0].status === "Error") {
    return (
      <ErrorCard>
        <StyledError
          alignItems="center"
          justifyContent="center"
          textAlign={"center"}
          flexDirection="column"
        >
          <StyledIcon size="24" mb={3}></StyledIcon>
          <Body mx={[3, 3, 7, 7]} mt={3} color="chalk">
            Sorry, an unexpected error occured while processing your clip
          </Body>
        </StyledError>
      </ErrorCard>
    );
  }

  return (
    <>
      <Modal isModalOpen={isShareModalOpen} setModalOpen={setShareModalOpen}>
        <ShareModal clip={clip} aspectRatio="9:16" isMobile />
      </Modal>
      <CardWhole>
        <Card
          thumbnail={clip.mobile[0].clipImageThumb}
          onClick={() => {
            history.push(`/clip?clip=${clip.shareId}&mobileView=true`);
          }}
        >
          <ShadowBackground>
            <StyledFlex
              alignItems={"center"}
              justifyContent={"center"}
              flexDirection={"column"}
            >
              <Flex
                mt={props.mt}
                justifyContent={"flex-start"}
                width={"100%"}
                ml={4}
                mb={[3, 3]}
              >
                <Flex
                  alignItems={"flex-start"}
                  flexDirection={"column"}
                  width={"80%"}
                >
                  <Flex flexDirection="row">
                    {hasStudio && !isRemixed && (
                      <Flex alignItems="center" mr={1}>
                        <StudioBadge
                          style={{ height: "20px", width: "20px" }}
                        />
                      </Flex>
                    )}
                    {isRemixed && (
                      <Flex alignItems="center">
                        <RemixIcon style={{ height: "20px", width: "20px" }} />
                      </Flex>
                    )}
                  </Flex>
                  <StyledHeadline mt={2} color={"ice"} className={"title"}>
                    {clip.userClipTitle
                      ? clip.userClipTitle
                      : getClipTitle(clip)}
                  </StyledHeadline>
                </Flex>
              </Flex>
            </StyledFlex>
          </ShadowBackground>
        </Card>
        <StyledOptions
          flexDirection="column"
          alignItems="center"
          justifyContent="space-evenly"
        >
          <Flex
            mb={2}
            flexDirection="row"
            justifyContent="space-evenly"
            width="100%"
          >
            <YouTubeShare
              mr={3}
              aspectRatio="9:16"
              size="social"
              classification="youTube"
              clip={clip}
              key={socialPlatformKeys.YouTube}
              onReset={onSocialPlatformReset}
            ></YouTubeShare>
            <TikTokUpload
              mr={3}
              ml={3}
              aspectRatio="9:16"
              size="social"
              classification="youTube"
              clip={clip}
              key={socialPlatformKeys.TikTok}
              onReset={onSocialPlatformReset}
            />
            <DownloadClip
              size="social"
              title="Download Mobile Clip"
              classification="round"
              href={`${process.env.REACT_APP_S3_CLIP_BUCKET}/${clip.mobile[0].clipLink}`}
              target="_blank"
              download
              onClick={() => {
                trackClipDownload(clip, clip.mobile[0].aspectRatio, {
                  user: user.username || user._id,
                  originalClip: clip.shareId,
                });
              }}
            ></DownloadClip>
          </Flex>
          <StyledLine mt={1} />
        </StyledOptions>
      </CardWhole>
    </>
  );
});
export { MobileClipCard };
